import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ClipboardList, Search, ScanSearch, Layers, Target, Waypoints, FileWarning,
  PenLine, Loader2, ChevronDown,
} from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import { useAuth } from '@/contexts/AuthContext';
import { WalletIntelSubNav } from '@/components/wallet-intel/SubNav';
import { AUDIT_ACTION_LABEL } from '@/types/walletIntel';
import type { AuditLogEntry } from '@/types/walletIntel';

const ACTIONS = Object.keys(AUDIT_ACTION_LABEL);

const ACTION_ICON: Record<string, typeof Search> = {
  lookup: Search,
  screen: ScanSearch,
  screen_batch: Layers,
  exposure_check: Target,
  cluster_expand: Waypoints,
  sar_export: FileWarning,
  add_manual_entry: PenLine,
};

const ACTION_COLOR: Record<string, string> = {
  lookup: '#00C8FF',
  screen: '#2979FF',
  screen_batch: '#2979FF',
  exposure_check: '#F0B429',
  cluster_expand: '#C026D3',
  sar_export: '#FF6B1A',
  add_manual_entry: '#00C853',
};

function shorten(addr: string): string {
  if (addr.length <= 20) return addr;
  return `${addr.slice(0, 10)}...${addr.slice(-6)}`;
}

function detailFor(entry: AuditLogEntry): string {
  const m = entry.metadata || {};
  switch (entry.action) {
    case 'lookup':
      return `${m.match_count ?? 0} citation${m.match_count === 1 ? '' : 's'} found`;
    case 'screen':
      return m.verdict === 'flagged'
        ? `Flagged — ${(m.reasons ?? []).map((r: string) => r.replace(/_/g, ' ')).join(', ')}`
        : m.verdict === 'needs_review'
        ? 'Needs review — a check errored, not a clean result'
        : 'Clear';
    case 'screen_batch':
      return `Part of a ${m.batch_size ?? '?'}-address batch — ${
        m.verdict === 'flagged' ? 'flagged' : m.verdict === 'needs_review' ? 'needs review' : 'clear'
      }`;
    case 'exposure_check': {
      const hops = m.hops_examined ?? m.hop ?? 0;
      return m.flagged
        ? `Indirect exposure found within ${hops} hop${hops === 1 ? '' : 's'}`
        : `No exposure within ${hops} hop${hops === 1 ? '' : 's'}`;
    }
    case 'cluster_expand':
      return `Explored ${m.node_count ?? '?'} node${m.node_count === 1 ? '' : 's'}${m.flagged_count ? ` · ${m.flagged_count} flagged` : ''}`;
    case 'sar_export':
      return `Report ${m.report_id ? String(m.report_id).slice(0, 8) : ''} — ${m.verdict ?? '?'}${m.case_id ? ` · case ${m.case_id}` : ''}`;
    case 'add_manual_entry':
      return `${m.category ?? 'citation'}${m.case_id ? ` · case ${m.case_id}` : ''}`;
    default:
      return '';
  }
}

export default function WalletAuditTrail() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [action, setAction] = useState('');
  const [chain, setChain] = useState('');
  const [address, setAddress] = useState('');
  const [caseId, setCaseId] = useState('');
  const [userId, setUserId] = useState('');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);

  const filters = {
    action: action || undefined,
    chain: chain || undefined,
    address: address.trim() || undefined,
    case_id: caseId.trim() || undefined,
    user_id: isAdmin ? (userId.trim() || undefined) : undefined,
    before: cursors[page],
    limit: 25,
  };

  const query = useQuery({
    queryKey: ['wallet-intel-audit-log', filters],
    queryFn: () => walletIntelApi.auditLog(filters),
  });

  function resetPaging() {
    setCursors([undefined]);
    setPage(0);
  }

  function nextPage() {
    if (!query.data?.next_cursor) return;
    setCursors((prev) => {
      const next = [...prev];
      next[page + 1] = query.data!.next_cursor!;
      return next;
    });
    setPage((p) => p + 1);
  }

  const inputStyle: React.CSSProperties = {
    background: '#0C1118', border: '1px solid #182030', color: '#DCE8F5', fontSize: 12.5,
  };

  return (
    <div style={{ background: '#050709', minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WalletIntelSubNav />

      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }} className="px-6 py-4 mb-6">
        <h1 style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>Audit Trail</h1>
        <p style={{ color: '#8D96AC', fontSize: 13, marginTop: 3 }}>
          Every lookup, screen, exposure, cluster, and SAR/STR action against this module — logged automatically as it happens.
          {!isAdmin && ' Showing your own activity.'}
        </p>
      </div>

      {/* Filters */}
      <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: 16 }} className="flex flex-wrap items-center gap-3 mb-5">
        <select value={action} onChange={(e) => { setAction(e.target.value); resetPaging(); }} className="rounded-lg px-3 py-2 outline-none" style={inputStyle}>
          <option value="">All actions</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{AUDIT_ACTION_LABEL[a]}</option>)}
        </select>
        <select value={chain} onChange={(e) => { setChain(e.target.value); resetPaging(); }} className="rounded-lg px-3 py-2 outline-none" style={inputStyle}>
          <option value="">All chains</option>
          <option value="bitcoin">Bitcoin</option>
          <option value="ethereum">Ethereum</option>
          <option value="tron">Tron</option>
        </select>
        <input
          value={address} onChange={(e) => { setAddress(e.target.value); resetPaging(); }}
          placeholder="Filter by address" className="rounded-lg px-3 py-2 outline-none font-mono" style={{ ...inputStyle, minWidth: 200 }}
        />
        <input
          value={caseId} onChange={(e) => { setCaseId(e.target.value); resetPaging(); }}
          placeholder="Filter by case ID" className="rounded-lg px-3 py-2 outline-none" style={{ ...inputStyle, width: 150 }}
        />
        {isAdmin && (
          <input
            value={userId} onChange={(e) => { setUserId(e.target.value); resetPaging(); }}
            placeholder="Filter by analyst user ID (admin)" className="rounded-lg px-3 py-2 outline-none font-mono" style={{ ...inputStyle, minWidth: 220 }}
          />
        )}
      </div>

      {query.isLoading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={22} style={{ color: '#00C8FF' }} /></div>
      ) : query.isError ? (
        <div style={{ background: '#0C1118', border: '1px solid #FF1F1F30', borderRadius: 12, padding: 24 }}>
          <p style={{ color: '#FF1F1F', fontSize: 13.5 }}>{(query.error as Error)?.message ?? 'Failed to load the audit trail.'}</p>
        </div>
      ) : (query.data?.entries.length ?? 0) === 0 ? (
        <div style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 12, padding: '56px 32px', textAlign: 'center' }}>
          <ClipboardList size={26} style={{ color: '#3A5A7A', margin: '0 auto 12px' }} />
          <p style={{ color: '#8D96AC', fontSize: 13.5 }}>
            {page > 0 ? 'No more entries.' : 'No activity matches these filters yet.'}
          </p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }} className="flex flex-col gap-2.5">
          {query.data!.entries.map((entry, i) => {
            const Icon = ACTION_ICON[entry.action] ?? ClipboardList;
            const c = ACTION_COLOR[entry.action] ?? '#5A7A9A';
            return (
              <motion.div
                key={`${entry.created_at}-${i}`}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: Math.min(i * 0.015, 0.3) }}
                style={{ background: '#0C1118', border: '1px solid #182030', borderLeft: `3px solid ${c}`, borderRadius: 10, padding: '13px 16px' }}
                className="flex items-center gap-4 flex-wrap"
              >
                <div style={{ width: 34, height: 34, borderRadius: 9, background: `${c}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={16} style={{ color: c }} />
                </div>
                <div className="flex flex-col gap-0.5" style={{ minWidth: 150 }}>
                  <span style={{ color: '#F2F6FC', fontWeight: 700, fontSize: 13 }}>{AUDIT_ACTION_LABEL[entry.action] ?? entry.action}</span>
                  <span style={{ color: '#5A7A9A', fontSize: 11 }}>{new Date(entry.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 12.5 }} title={entry.address}>
                    {shorten(entry.address)} <span style={{ color: '#5A7A9A', fontFamily: 'inherit' }}>· {entry.chain}</span>
                  </span>
                  <span style={{ color: '#8D96AC', fontSize: 11.5 }}>{detailFor(entry)}</span>
                </div>
                {isAdmin && (
                  <span style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 600, flexShrink: 0 }}>
                    {entry.user_display || entry.user_id.slice(0, 8)}
                  </span>
                )}
              </motion.div>
            );
          })}

          {query.data?.next_cursor && (
            <div className="flex justify-center py-2">
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={nextPage}
                disabled={query.isFetching}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold tracking-wide"
                style={{ background: '#0F2030', color: '#00C8FF', border: '1px solid #1E3A5F', fontSize: 12 }}
              >
                {query.isFetching ? <Loader2 size={13} className="animate-spin" /> : <ChevronDown size={13} />}
                LOAD MORE
              </motion.button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
